import React from 'react';
import Image from 'next/image';
import { useTranslations } from 'use-intl';
import LoadingIcon from 'components/commercetools-ui/atoms/button/loadingIcon';
import useMediaClipThumbnail from 'helpers/hooks/useMediaclipThumbnail';
import useUserImageUrl from 'helpers/hooks/useUserImageUrl';
import { PLUSICON } from './constants';
import { LineItem } from './types';

export type ProductStripProps = {
  items: LineItem[];
  idx?: number;
};

type StripImageTileProps = {
  item: LineItem;
  idx?: number;
  index: number;
  noImageLabel: string;
  transformImageUrl: (url: string) => string;
};

const StripImageTile: React.FC<StripImageTileProps> = ({ item, idx, index, noImageLabel, transformImageUrl }) => {
  const mediaClipProjectId = item?.custom?.fields.mediaClipProjectId;

  const thumb = useMediaClipThumbnail(mediaClipProjectId || '');

  const alt = item.name || item.variant?.sku || 'Product image';

  let src = '';
  let loading = false;

  if (mediaClipProjectId) {
    src = thumb?.imageSrc || '';
    loading = thumb?.loading || false;
  } else if (item.imageMetadata?.urlThumb) {
    src = transformImageUrl(item.imageMetadata.urlThumb);
  } else {
    const imgList = Array.isArray(item.variant?.images) ? item.variant.images : [];
    src = imgList[0] || '';
  }

  if (loading) {
    return (
      <div className="flex size-full flex-1 items-center justify-center text-[#333]">
        <LoadingIcon className="fill-gray-700" />
      </div>
    );
  }

  if (src) {
    return (
      <Image
        src={src}
        data-ffid={`img-minicart-thumbnail-image-${idx}-${index}`}
        alt={alt}
        width={88}
        height={87}
        className="size-full object-cover"
        loading="lazy"
        unoptimized={true}
      />
    );
  }

  return (
    <div
      className="flex size-full flex-1 items-center justify-center text-center text-[#333]"
      data-ffid={`img-minicart-thumbnail-no-image-${idx}-${index}`}
    >
      {noImageLabel}
    </div>
  );
};

export const ProductStrip: React.FC<ProductStripProps> = ({ items, idx }) => {
  const translate = useTranslations();
  const { getUserImageUrl } = useUserImageUrl();
  const noImageLabel = translate('cart.no-image');

  const totalImages = items.length;
  const showHiddenTile = totalImages > 3;
  const visibleItemsCount = showHiddenTile ? 2 : Math.min(totalImages, 3);
  const visibleItems = items.slice(0, visibleItemsCount);
  const hiddenCount = totalImages - visibleItemsCount;

  const renderTile = (
    key: string,
    children: React.ReactNode,
    className?: string,
    testId?: string,
    ariaLabel?: string,
  ) => (
    <li
      key={key}
      className={`flex-0 h-87 w-88 rounded-lg border border-[#EBEBEB] bg-white ${className}`}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {children}
    </li>
  );

  return (
    <ul className="mt-3 flex flex-1 justify-start gap-14" data-testid="product-strip">
      {totalImages === 0 ? (
        renderTile(
          'empty-tile',
          noImageLabel,
          'overflow-hidden p-5 flex items-center justify-center text-center text-[#333]'
        )
      ) : (
        <>
          {visibleItems.map((item, index) =>
            renderTile(
              `${item.id || index}-${index}`,
              <StripImageTile
                item={item}
                idx={idx}
                index={index}
                noImageLabel={noImageLabel}
                transformImageUrl={getUserImageUrl}
              />,
              'overflow-hidden p-5',
              'thumb',
            ),
          )}

          {showHiddenTile &&
            renderTile(
              `hidden-count-tile-${hiddenCount}`,
              (
                <span
                  className="font-normal text-[#333]"
                  data-ffid={`img-minicart-thumbnail-hidden-${idx}-${visibleItemsCount}`}
                >
                  {PLUSICON}
                  {hiddenCount}
                </span>
              ),
              'flex items-center justify-center p-16',
              'hidden-count-tile',
              `${translate('cart.hidden-items-count')} ${hiddenCount}`,
            )}
        </>
      )}
    </ul>
  );
};
